<template>
    <div>
    <!-- Loading State -->
    <div
        v-if="loading"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12"
    >
        <div
            v-for="n in 8"
            :key="n"
            class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-8 animate-pulse"
        >
            <div class="flex items-start justify-between mb-6">
                <div class="w-20 h-16 bg-gray-200 rounded"></div>
                <div class="w-6 h-6 bg-gray-200 rounded-full"></div>
            </div>
            <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
        </div>
    </div>

    <!-- Empty State for Favorites -->
    <div
        v-else-if="!loading && filters.favorites && paginatedPrograms.length === 0"
        class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
    >
        <div
            class="w-16 h-16 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700"
        >
            <Icon icon="unfavorited" class="w-8 h-8 text-slate-800 dark:text-slate-100" />
        </div>
        <div class="flex flex-col items-center pt-6 gap-2">
            <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                No favorite programs yet
            </h3>
            <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center">
                Tap the heart button on any program to save it to your
                favorites.
            </p>
        </div>
        <div class="flex flex-col items-start pt-6">
            <button
                @click="$emit('toggle-favorites-filter')"
                class="flex items-start gap-2 px-4 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
            >
                Go to transfer partners
            </button>
        </div>
    </div>

    <!-- Empty State for Search -->
    <div
        v-else-if="!loading && debouncedQuery && debouncedQuery.trim() !== '' && paginatedPrograms.length === 0"
        class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
    >
        <div
            class="w-16 h-16 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700"
        >
            <Icon icon="search" class="w-8 h-8 text-slate-800 dark:text-slate-100" />
        </div>
        <div class="flex flex-col items-center pt-6 gap-2">
            <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                No matching programs found
            </h3>
            <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center">
                Try changing your search parameters.
            </p>
        </div>
    </div>

    <!-- Empty State for Filters -->
    <div
        v-else-if="!loading && !filters.favorites && hasFilterSelections && paginatedPrograms.length === 0"
        class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
    >
        <div
            class="w-16 h-16 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700"
        >
            <Icon icon="filters" class="w-8 h-8 text-slate-800 dark:text-slate-100" />
        </div>
        <div class="flex flex-col items-center pt-6 gap-2">
            <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                No matching programs found
            </h3>
            <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center">
                Try removing some filters to see more programs.
            </p>
        </div>
        <div class="flex flex-col items-start pt-6">
            <button
                @click="clearAllFilters()"
                class="flex items-start gap-2 px-4 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
            >
                Clear filters
            </button>
        </div>
    </div>

    <!-- Mobile Grouped List (Favorites + grouped by Program type) -->
    <div
        v-else-if="!loading && isMobile && groupedPrograms && filters.favorites"
        class="md:hidden mb-8"
    >
        <div
            v-for="(programs, type) in groupedPrograms"
            :key="type"
            class="mb-6"
        >
            <div class="flex py-2 pb-4 items-center gap-3 mb-2">
                <Icon
                    :icon="getTypeDisplayInfo(type).icon"
                    class="w-5 h-5 text-slate-800 dark:text-slate-100"
                />
                <h2 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6">
                    {{ getTypeDisplayInfo(type).name }}
                </h2>
            </div>
            <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <TransferPartnerMobileRow
                    v-for="partner in programs"
                    :key="partner.id"
                    :partner="partner"
                    :alertsFeatureEnabled="alertsFeatureEnabled"
                    :alertsEnabledProgramIds="alertsEnabledProgramIds"
                    :imageError="imageError"
                    :formatProgramType="formatProgramType"
                    @toggle-favorite="$emit('toggle-favorite', $event)"
                    @navigate="$emit('navigate', $event)"
                    @image-error="$emit('image-error', $event)"
                />
            </div>
        </div>
    </div>

    <!-- Mobile List (single white card with separators) -->
    <div v-else-if="!loading && isMobile" class="md:hidden mb-8">
        <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <TransferPartnerMobileRow
                v-for="partner in filters.favorites ? sortedPrograms : paginatedPrograms"
                :key="partner.id"
                :partner="partner"
                :alertsFeatureEnabled="alertsFeatureEnabled"
                :alertsEnabledProgramIds="alertsEnabledProgramIds"
                :imageError="imageError"
                :formatProgramType="formatProgramType"
                @toggle-favorite="$emit('toggle-favorite', $event)"
                @navigate="$emit('navigate', $event)"
                @image-error="$emit('image-error', $event)"
            />
        </div>
    </div>

    <!-- Desktop Grid - Grouped View -->
    <div
        v-else-if="!loading && groupedPrograms && filters.favorites"
        class="hidden md:block mb-12"
    >
        <div
            v-for="(programs, type) in groupedPrograms"
            :key="type"
            class="mb-8"
        >
            <div class="flex py-2 pb-4 items-center gap-3 mb-4">
                <Icon
                    :icon="getTypeDisplayInfo(type).icon"
                    class="w-5 h-5 text-slate-800 dark:text-slate-100"
                />
                <h2 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                    {{ getTypeDisplayInfo(type).name }}
                </h2>
            </div>
            <div class="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-8">
                <TransferPartnerCard
                    v-for="(partner, index) in programs"
                    :key="partner.id"
                    :partner="partner"
                    :index="index"
                    :alertsFeatureEnabled="alertsFeatureEnabled"
                    :alertsEnabledProgramIds="alertsEnabledProgramIds"
                    :imageError="imageError"
                    :formatProgramType="formatProgramType"
                    @toggle-favorite="$emit('toggle-favorite', $event)"
                    @navigate="$emit('navigate', $event)"
                    @image-error="$emit('image-error', $event)"
                />
            </div>
        </div>
    </div>

    <!-- Desktop Grid - Normal View -->
    <div
        v-else-if="!loading"
        class="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-8 mb-12"
    >
        <TransferPartnerCard
            v-for="(partner, index) in filters.favorites ? sortedPrograms : paginatedPrograms"
            :key="partner.id"
            :partner="partner"
            :index="index"
            :alertsFeatureEnabled="alertsFeatureEnabled"
            :alertsEnabledProgramIds="alertsEnabledProgramIds"
            :imageError="imageError"
            :formatProgramType="formatProgramType"
            @toggle-favorite="$emit('toggle-favorite', $event)"
            @navigate="$emit('navigate', $event)"
            @image-error="$emit('image-error', $event)"
        />
    </div>

    <!-- Pagination (desktop) -->
    <div
        v-if="!loading && totalPages > 1"
        class="mt-8 custom-pagination hidden md:block"
    >
        <Pagination
            :totalResults="allPrograms.length"
            :totalPages="totalPages"
            :perPage="itemsPerPage"
            :currentPage="currentPage"
            :maxVisibleButtons="5"
            @pageChanged="$emit('page-change', $event)"
        />
    </div>

    <!-- Mobile Pagination -->
    <div
        v-if="!loading && totalPages > 1"
        class="mt-8 custom-pagination md:hidden"
    >
        <nav
            class="flex justify-center items-center rounded text-slate-800 dark:text-slate-100 font-normal text-sm space-x-1"
            aria-label="Pagination"
        >
            <button
                @click="$emit('page-change', currentPage - 1)"
                :disabled="currentPage === 1"
                class="h-9 flex py-2 px-1.5 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent text-slate-800 dark:text-slate-100 disabled:!text-slate-800 dark:disabled:!text-slate-500 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-700 focus:border-slate-300 dark:focus:border-slate-600 focus:outline-0"
            >
                <span class="sr-only">Previous</span>
                <Icon icon="chevron-right" svgClass="w-4 h-4 rotate-180" />
            </button>
            <button
                v-for="page in mobilePages"
                :key="page"
                @click="$emit('page-change', page)"
                aria-current="page"
                class="h-9 flex p-2 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent focus:z-20 focus:border-slate-300 dark:focus:border-slate-600 focus:outline-0 active:border-transparent"
                :class="[ Number(page) === currentPage ? 'text-orange-500 bg-gray-100 dark:bg-slate-700 font-medium' : 'text-slate-800 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-700', ]"
            >
                {{ page }}
            </button>
            <button
                @click="$emit('page-change', currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="h-9 flex py-2 px-1.5 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent text-slate-800 dark:text-slate-100 disabled:!text-slate-800 dark:disabled:!text-slate-500 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-700 focus:border-slate-300 dark:focus:border-slate-600 focus:outline-0"
            >
                <span class="sr-only">Next</span>
                <Icon icon="chevron-right" svgClass="w-4 h-4" />
            </button>
        </nav>
    </div>

    <!-- Mobile range text under pagination -->
    <div
        v-if="!loading && allPrograms.length > 0"
        class="md:hidden mt-4 text-center"
    >
        <p class="text-slate-500 dark:text-slate-400 text-base">
            Showing {{ startIndex }}-{{ endIndex }} of
            {{ allPrograms.length }} programs
        </p>
    </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import TransferPartnerCard from './TransferPartnerCard.vue';
import TransferPartnerMobileRow from './TransferPartnerMobileRow.vue';

defineProps({
    loading: { type: Boolean, default: false },
    filters: { type: Object, required: true },
    isMobile: { type: Boolean, default: false },
    paginatedPrograms: { type: Array, default: () => [] },
    sortedPrograms: { type: Array, default: () => [] },
    groupedPrograms: { type: Object, default: null },
    allPrograms: { type: Array, default: () => [] },
    hasFilterSelections: { type: Boolean, default: false },
    debouncedQuery: { type: String, default: '' },
    alertsFeatureEnabled: { type: Boolean, default: false },
    alertsEnabledProgramIds: { type: Array, default: () => [] },
    imageError: { type: Object, default: () => ({}) },
    formatProgramType: { type: Function, required: true },
    getTypeDisplayInfo: { type: Function, required: true },
    clearAllFilters: { type: Function, required: true },
    // Pagination
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    itemsPerPage: { type: Number, default: 24 },
    startIndex: { type: Number, default: 0 },
    endIndex: { type: Number, default: 0 },
    mobilePages: { type: Array, default: () => [] },
});

defineEmits([
    'toggle-favorite',
    'navigate',
    'image-error',
    'page-change',
    'toggle-favorites-filter',
]);
</script>

<style scoped>
.custom-pagination :deep(nav) {
    gap: 8px;
}

.custom-pagination :deep(button[aria-current='page']) {
    min-width: 36px;
    padding: 8px;
    border-radius: 4px;
}

.custom-pagination :deep(button[aria-current='page'].text-orange-500) {
    background-color: #1e293b !important;
    color: white !important;
    font-weight: 500;
}

.custom-pagination :deep(button[aria-current='page']:not(.text-orange-500)) {
    background-color: transparent;
    color: #1e293b;
}

.custom-pagination
    :deep(button[aria-current='page']:not(.text-orange-500):hover) {
    background-color: #f1f5f9;
}

/* Dark mode: inactive page numbers were forced to #1e293b (invisible on
   the dark page). The .dark ancestor sits on <html>; .custom-pagination
   carries this component's scope id so this selector resolves correctly. */
.dark .custom-pagination :deep(button[aria-current='page'].text-orange-500) {
    background-color: #334155 !important;
}

.dark
    .custom-pagination
    :deep(button[aria-current='page']:not(.text-orange-500)) {
    color: #cbd5e1;
}

.dark
    .custom-pagination
    :deep(button[aria-current='page']:not(.text-orange-500):hover) {
    background-color: #334155;
    color: #f1f5f9;
}
</style>
